<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title" />
    <div class="predefined-lot-numbers-add-edit-container" style="display: flex; gap: 24px">
      <StepsPanelWrapper
        ref="stepsWrapper"
        :steps="steps"
        :current-step.sync="currentStep"
        :step-props-fn="getStepProps"
        @step-input="onStepInput"
        @validation-error="handleValidationError"
      />
      <RightInfoPanelGeneric
        v-model="rightPanelData"
        :title-key="isEdit ? 'stock_management.predefined_lot_numbers.edit' : 'stock_management.predefined_lot_numbers.create_new'"
        :items="rightPanelItems"
        :saving="saving"
        :show-save-and-new="true"
        @save="save"
        @saveAndNew="saveAndNew"
        @back="gotoList"
      />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { canEdit, permissions, permissionTypes } from "@/helpers/Permissions";
import { errorMessage, handlePromiseException } from "@/helpers/common";
import Forbidden from "@/pages/errors/Forbidden.vue";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import StepsPanelWrapper from "@/components/Common/StepsPanelWrapper.vue";
import RightInfoPanelGeneric from "@/components/Common/RightInfoPanelGeneric.vue";
import PredefinedLotNumberForm from "./partials/form.vue";
import notify from "devextreme/ui/notify";

export default {
  name: "PredefinedLotNumbersAddOrEdit",
  components: {
    Forbidden,
    EditCreateTitleBox,
    StepsPanelWrapper,
    RightInfoPanelGeneric,
    PredefinedLotNumberForm,
  },
  data() {
    return {
      data: {
        product_uuid: null,
        product_name: null,
        product_code: null,
        parcel_uuid: null,
        parcel_name: null,
        parcel_uuids: [],
        parcel_names: [],
        lot_number: null,
      },
      seasons: [],
      steps: [
        {
          number: 1,
          label: this.$t("stock_management.predefined_lot_numbers.title"),
          component: PredefinedLotNumberForm,
          ref: "PredefinedLotNumberForm",
        },
      ],
      currentStep: 1,
      saving: false,
    };
  },
  computed: {
    isEdit() {
      return this.$route.params.uuid && this.$route.params.uuid !== "add";
    },
    config() {
      return {
        permission: permissions.SM_PREDEFINED_LOT_NUMBER,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
    title() {
      return this.isEdit
        ? this.$t("stock_management.predefined_lot_numbers.edit")
        : this.$t("stock_management.predefined_lot_numbers.create_new");
    },
    rightPanelData() {
      return this.data;
    },
    rightPanelItems() {
      return [
        {
          labelKey: "stock_management.predefined_lot_numbers.product",
          path: "product_name",
          required: true,
        },
        {
          labelKey: "stock_management.predefined_lot_numbers.lot_number",
          path: "lot_number",
          required: true,
        },
        {
          labelKey: "stock_management.predefined_lot_numbers.parcel",
          path: "parcel_name",
        },
      ];
    },
  },
  async mounted() {
    await this.loadSeasons();
    if (this.isEdit) {
      await this.loadData();
    }
  },
  methods: {
    ...mapActions({
      getPredefinedLotNumberByUuid: "smPredefinedLotNumbers/getPredefinedLotNumberByUuid",
      createPredefinedLotNumber: "smPredefinedLotNumbers/createPredefinedLotNumber",
      updatePredefinedLotNumber: "smPredefinedLotNumbers/updatePredefinedLotNumber",
      getSeasons: "season/getSeasons",
    }),
    async loadSeasons() {
      try {
        const res = await this.getSeasons({ limit: 9999 });
        this.seasons = res?.data?.data ?? [];
      } catch (e) {
        console.error("Error loading seasons:", e);
      }
    },
    async loadData() {
      try {
        const res = await this.getPredefinedLotNumberByUuid({ uuid: this.$route.params.uuid });
        this.data = { ...this.data, ...(res?.data?.data ?? res?.data ?? {}) };
      } catch (e) {
        console.error("Error loading predefined lot number:", e);
        errorMessage(this, this.$t("common.error_loading_data"));
      }
    },
    getStepProps(step) {
      if (!step) return {};
      if (step.number === 1) {
        return {
          value: this.data,
          seasons: this.seasons,
          isEdit: this.isEdit,
        };
      }
      return {};
    },
    onStepInput(step, payload) {
      if (step && step.number === 1) {
        this.data = { ...this.data, ...(payload || {}) };
      }
    },
    validateData() {
      const errors = [];
      if (!this.data.product_uuid) {
        errors.push(this.$t("common.required_field", { field: this.$t("stock_management.predefined_lot_numbers.product") }));
      }
      if (!this.data.lot_number || !this.data.lot_number.trim()) {
        errors.push(this.$t("common.required_field", { field: this.$t("stock_management.predefined_lot_numbers.lot_number") }));
      }
      return errors;
    },
    handleValidationError(error) {
      errorMessage(this, error.message || this.$t("fill_out_error"));
    },
    gotoList() {
      this.$router.push({ name: "predefined-lot-numbers" });
    },
    async save() {
      const errors = this.validateData();
      if (errors.length) {
        errors.forEach((msg) => errorMessage(this, msg));
        return;
      }
      this.saving = true;
      try {
        let res;
        const payload = {
          product_uuid: this.data.product_uuid,
          lot_number: this.data.lot_number,
        };
        if (this.isEdit) {
          payload.parcel_uuid = this.data.parcel_uuid || null;
          payload.uuid = this.$route.params.uuid;
          res = await this.updatePredefinedLotNumber(payload);
        } else {
          payload.parcel_uuids = this.data.parcel_uuids || [];
          res = await this.createPredefinedLotNumber(payload);
        }
        notify({
          message: res?.message ?? this.$t("success"),
          type: "success",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
        this.gotoList();
      } catch (e) {
        console.error("Error saving predefined lot number:", e);
        handlePromiseException(this, e);
      } finally {
        this.saving = false;
      }
    },
    async saveAndNew() {
      const errors = this.validateData();
      if (errors.length) {
        errors.forEach((msg) => errorMessage(this, msg));
        return;
      }
      this.saving = true;
      try {
        const payload = {
          product_uuid: this.data.product_uuid,
          lot_number: this.data.lot_number,
        };
        if (this.isEdit) {
          payload.parcel_uuid = this.data.parcel_uuid || null;
          payload.uuid = this.$route.params.uuid;
          await this.updatePredefinedLotNumber(payload);
        } else {
          payload.parcel_uuids = this.data.parcel_uuids || [];
          await this.createPredefinedLotNumber(payload);
        }
        notify({
          message: this.$t("common.save_success"),
          type: "success",
          displayTime: 2500,
          width: "auto",
          position: { at: "top center", my: "top center" },
        });
        // Reset parcel and lot_number but keep product
        this.data = {
          ...this.data,
          parcel_uuid: null,
          parcel_name: null,
          parcel_uuids: [],
          parcel_names: [],
          lot_number: null,
          uuid: null,
        };
        if (this.isEdit) {
          this.$router.push({ name: "predefined-lot-numbers-edit", params: { uuid: "add" } });
        }
      } catch (e) {
        console.error("Error saving predefined lot number:", e);
        handlePromiseException(this, e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}
.predefined-lot-numbers-add-edit-container {
  display: flex;
  height: 100%;
  width: 100%;
}
</style>
